import type { LensCardData } from '~/types/leLens';

import type { LensDateRange } from './cardView';
import { formatResolvedPeriod } from './resolvedPeriod';

export type LensReaderScope =
  | { lensId: string; ready: false; period?: never; filters?: never }
  | {
      lensId: string;
      ready: true;
      period: { from?: string; to?: string; summary: string };
      filters: Array<{ label: string; value: string }>;
    };

/** Previous-result placeholders cannot establish the scope of a new Lens or filter request. */
export function buildLensReaderScope({
  lensId,
  loadedLensId,
  range,
  filters,
  hasPreviousData,
  selectionPending,
  boardResolved,
  cards,
}: {
  lensId: string;
  loadedLensId: string | undefined;
  range: LensDateRange;
  filters: Array<{ label: string; value: string }>;
  hasPreviousData: boolean;
  selectionPending: boolean;
  boardResolved: boolean;
  cards?: ReadonlyArray<Pick<LensCardData, 'period' | 'unbound'>>;
}): LensReaderScope {
  if (loadedLensId !== lensId || !boardResolved || hasPreviousData || selectionPending) {
    return { lensId, ready: false };
  }
  const matching =
    cards?.filter(
      (card) =>
        !card.unbound && card.period && card.period.current.from === range.from && card.period.current.to === range.to,
    ).length ?? 0;
  if (!matching) {
    const allUnbound = cards && cards.length > 0 && cards.every((card) => card.unbound);
    const hasOwnPeriods = cards?.some((card) => !card.unbound && card.period);
    return {
      lensId,
      ready: true,
      period: {
        summary: allUnbound
          ? 'Dates come from the saved queries; the dashboard period does not apply to these results.'
          : hasOwnPeriods
            ? 'Cards use their own resolved periods, which differ from the dashboard period. Check each card’s period metadata.'
            : 'The executed card dates are not confirmed. Check the saved query evidence.',
      },
      filters,
    };
  }
  const mixed = cards && matching < cards.length;
  const summary = mixed
    ? `${formatResolvedPeriod(range)}. This dashboard period applies only to period-bound cards whose resolved dates match it; other cards use their saved queries, their own resolved periods, or have unconfirmed dates.`
    : formatResolvedPeriod(range);
  return { lensId, ready: true, period: { ...range, summary }, filters };
}
